import type { Detail } from "./designTypes";
import { navigate, repoPath, useCurrentRepo } from "./router";
import { AdrLink } from "./AdrLink";
import { useAuthedAssetURL } from "./useAuthedAssetURL";
import { apiPath, useApiResource } from "./useApiResource";

// DesignOriginalLink only exists to isolate the useAuthedAssetURL call —
// mounting it conditionally (kind === pdf/docx) is how DesignReader avoids
// fetching /original for markdown/html/other documents, since hooks can't be
// called conditionally within one component (same pattern as BitsGallery's
// BitThumb). Renders nothing while loading or on a fetch failure — matching
// BitReader, which also has no separate error message for its asset fetch.
function DesignOriginalLink({ id, token, kind }: { id: string; token: string; kind: string }) {
  const repo = useCurrentRepo();
  const { url } = useAuthedAssetURL(apiPath(repo, `/design/${encodeURIComponent(id)}/original`), token);
  if (!url) return null;
  return (
    <p>
      <a className="btn-ghost" href={url} target="_blank" rel="noopener noreferrer">
        Open {kind.toUpperCase()} in a new tab
      </a>
    </p>
  );
}

// DesignReader is the spec §6.3 design reader. Only Kind === "markdown"
// documents have rendered content (see internal/designs' package doc
// comment on the M9 preview-scoping decision) — everything else falls back
// to a source-link-only view, except PDF/DOCX, which additionally get an
// in-app "open original" link (user-requested; internal/server's
// GET /api/design/{id}/original, reusing the same on-demand-blob-fetch
// Store BitReader's PDF open link uses). HTML stays link-only: opening
// arbitrary repository-supplied HTML executes its script content with real
// browser privileges, and this app has no sandboxing story for that yet.
//
// The server sanitises `html` before it reaches this component (see
// ADRReader's docstring for the full trust-boundary writeup); this is safe
// only because of that upstream guarantee.
export function DesignReader({ token, id }: { token: string; id: string }) {
  const repo = useCurrentRepo();
  const { status, data, message } = useApiResource<Detail>(token, repo, `/design/${encodeURIComponent(id)}`);

  const backLink = (
    <a
      href={repoPath(repo, "design")}
      onClick={(e) => {
        e.preventDefault();
        navigate(repoPath(repo, "design"));
      }}
    >
      ← Back to design library
    </a>
  );

  if (status === "loading") {
    return (
      <section className="panel">
        {backLink}
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready" || !data) {
    return (
      <section className="panel">
        {backLink}
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  const toc = data.toc ?? [];
  const relatedAdrs = data.relatedAdrs ?? [];
  const relatedCapabilities = data.relatedCapabilities ?? [];
  const isMarkdown = data.kind === "markdown";

  return (
    <div className="doc-reader">
      <section className="panel doc-content">
        {backLink}
        <h2>{data.title}</h2>
        <p className="mono muted-note">
          {data.collection} · {data.kind}
        </p>
        {data.description && (
          <p className="muted-note">
            {data.description}
            {data.descriptionInferred ? " (inferred)" : ""}
          </p>
        )}

        {isMarkdown ? (
          // eslint-disable-next-line react/no-danger -- server-sanitised, see component docstring
          <div className="rendered-markdown" dangerouslySetInnerHTML={{ __html: data.html }} />
        ) : (
          <>
            <p className="muted-note">
              No inline preview is available for this document ({data.kind}) — view it on GitHub below.
            </p>
            {(data.kind === "pdf" || data.kind === "docx") && (
              <DesignOriginalLink id={id} token={token} kind={data.kind} />
            )}
          </>
        )}

        <h3>Related ADRs</h3>
        {relatedAdrs.length > 0 ? (
          <ul className="doc-toc-list">
            {relatedAdrs.map((a) => (
              <li key={a.id}>
                <AdrLink id={a.id} title={a.title} />{" "}
                {a.title || <span className="muted-note">(unresolved reference)</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted-note">None found.</p>
        )}

        <h3>Related capabilities</h3>
        {relatedCapabilities.length > 0 ? (
          <ul>
            {relatedCapabilities.map((c) => (
              <li key={c.id}>
                <span className="mono">{c.id}</span> {c.name}{" "}
                <span className="muted-note">(via {(c.sharedAdrs ?? []).join(", ")})</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted-note">None found.</p>
        )}

        <p>
          <a href={data.sourceUrl} target="_blank" rel="noopener noreferrer">
            View source on GitHub
          </a>
        </p>
      </section>

      {toc.length > 0 && (
        <nav className="doc-toc" aria-label="Table of contents">
          <h3>Contents</h3>
          <ul>
            {toc.map((h) => (
              <li key={h.id} className={h.level === 3 ? "toc-sub" : undefined}>
                <a href={`#${h.id}`}>{h.text}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
